import { completeCalcParams, type IndicatorStyleOverrides } from '@edge/charts-indicators'
import type { ChartSettings } from '../components/SettingsModal'
import type { Period } from '../utils/resolution'
import type { TimeframeVisibilityRule } from '../utils/timeframeVisibility'

/* ------------------------------------------------------------------ */
/*  Data shapes                                                        */
/* ------------------------------------------------------------------ */

/**
 * Indicator entry — persistence shape carrying both calculation params
 * and per-figure style overrides. Legacy forms:
 *   - bare string (just the indicator name)
 *   - { name, calcParams }
 * are still accepted on read; normalize via `normalizeIndicatorEntry`.
 */
export type IndicatorEntry =
  | string
  | {
      name: string
      calcParams?: unknown[]
      /** Per-figure style overrides keyed by figure key (see @edge/charts-indicators). */
      styles?: IndicatorStyleOverrides
      /** When false, the indicator stays in the panel state but is hidden on the chart. */
      visible?: boolean
      /** Where the indicator renders. Only meaningful for VOL: 'main' overlays
       *  it on the price pane, 'sub' (default) gives it its own bottom pane. */
      placement?: 'main' | 'sub'
      /** Per-timeframe visibility rule (the "Visibility" tab). When set,
       *  the indicator only draws on periods the rule permits, gated on top of
       *  `visible`. Undefined ⇒ visible on all timeframes. */
      timeframeVisibility?: TimeframeVisibilityRule
      /** Higher timeframe the indicator is COMPUTED from ('30m', '1H', '1D',
       *  '1W', …): multi-timeframe mode. Undefined ⇒ the chart's own timeframe.
       *  Distinct from `timeframeVisibility`, which decides where it is SHOWN.
       *  Only meaningful for MTF-eligible indicators (see isMtfEligibleIndicator). */
      sourceTimeframe?: string
      /** Persisted height of this sub indicator's pane as a fraction of the
       *  chart container height (0..1). Only meaningful for sub-pane indicators
       *  (RSI/MACD/VOL-sub/…); undefined ⇒ default height. Captured from
       *  separator drags, restored via ChartManager.setPaneHeight on load. */
      paneHeightRatio?: number
    }

export interface NormalizedIndicatorEntry {
  name: string
  calcParams?: unknown[]
  styles?: IndicatorStyleOverrides
  visible?: boolean
  placement?: 'main' | 'sub'
  timeframeVisibility?: TimeframeVisibilityRule
  sourceTimeframe?: string
  paneHeightRatio?: number
}

/** Accepts any legacy or current `IndicatorEntry` shape and returns the normalized object form. */
export function normalizeIndicatorEntry(entry: IndicatorEntry): NormalizedIndicatorEntry {
  if (typeof entry === 'string') return { name: entry }
  return {
    name: entry.name,
    // Repairs arrays saved short (see completeCalcParams) so they load drawn.
    calcParams: completeCalcParams(entry.name, entry.calcParams),
    styles: entry.styles,
    visible: entry.visible,
    placement: entry.placement,
    timeframeVisibility: entry.timeframeVisibility,
    sourceTimeframe: entry.sourceTimeframe,
    paneHeightRatio: entry.paneHeightRatio,
  }
}

export interface PanelState {
  period?: Period
  chartType?: string
  mainIndicators?: IndicatorEntry[]
  subIndicators?: IndicatorEntry[]
  settings?: Partial<ChartSettings>
  drawingBarVisible?: boolean
}

export interface TemplateData {
  templateId: string
  name: string
  chartType: string
  mainIndicators: string[]
  subIndicators: string[]
  settings: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/**
 * Overlay-agnostic style override we control — a small subset of klinecharts'
 * `OverlayStyle` surfaced in the drawing settings panel. Mapped to klinecharts'
 * `{ line, point }` shape via `ChartManager.overrideOverlayStyle` on apply;
 * `dashedValue` is left to the klinecharts default.
 */
export interface OverlayStyleOverride {
  color?: string
  size?: number
  style?: 'solid' | 'dashed'
  /**
   * Fill color for closed shapes (`FILLABLE_SHAPES`: rect / circle / ellipse /
   * triangle / polygon), set by the drawer's Background control. Absent means
   * outline only. Stored as the raw picker value (hex); composed with
   * `fillColorOpacity` into rgba at apply time by
   * `ChartManager.overlayStyleToKline`.
   */
  fillColor?: string
  /**
   * The fill's own opacity, 0..1, from the Background color picker. Separate
   * from `fillOpacity` so a solid border can sit over a translucent fill. When
   * unset (legacy and AI-drawn shapes) the fill alpha falls back to
   * `fillOpacity`, then `DEFAULT_FILL_OPACITY`.
   */
  fillColorOpacity?: number
  /**
   * The drawing's opacity, 0..1, despite the name: it alphas the stroke
   * (`color`), and the fill too when `fillColorOpacity` is unset. Edited by
   * the Line popover's Transparency steps and the Color/Border picker's
   * opacity slider. When unset the stroke is opaque.
   */
  fillOpacity?: number
}

export interface SavedDrawing {
  syncId: string
  name: string
  points: Array<{ timestamp: number; value: number }>
  /** Per-drawing style override (color / line width / line style). Absent on
   *  legacy drawings — they load with the overlay's default styling. */
  styles?: OverlayStyleOverride
  /** When false, the drawing is hidden on the chart but kept in state. */
  visible?: boolean
  /** When true, the drawing is locked (not draggable). */
  lock?: boolean
  /** Per-instance non-geometric state (text content, labels). See
   *  `OverlaySyncEvent.extendData`. */
  extendData?: unknown
}

/* ------------------------------------------------------------------ */
/*  Adapter interface                                                   */
/* ------------------------------------------------------------------ */

export interface ChartPersistence {
  /** Load per-chart panel state (indicators, chart type, settings) */
  loadPanel(chartId: string): Promise<PanelState | null>
  /** Save per-chart panel state */
  savePanel(chartId: string, data: PanelState): Promise<void>

  /** Load all user templates */
  loadTemplates(): Promise<TemplateData[]>
  /** Save a new template, returns the created template with server-assigned id */
  saveTemplate(
    data: Omit<TemplateData, 'templateId' | 'createdAt' | 'updatedAt'>,
  ): Promise<TemplateData>
  /** Delete a template by id */
  deleteTemplate(templateId: string): Promise<void>

  /** Load drawings for a ticker */
  loadDrawings(ticker: string): Promise<SavedDrawing[]>
  /** Save or update a drawing */
  saveDrawing(ticker: string, drawing: SavedDrawing): Promise<void>
  /** Delete a drawing */
  deleteDrawing(ticker: string, syncId: string): Promise<void>
}
